/**
 * Pure graph-mutation operations for automation flows.
 *
 * Extracted from `ListFlowBuilder` (Phase 0 of the builder 2.0 work) so the
 * 1.0 builder and the new 2.0 builder share one implementation of the tricky
 * connection-management logic — delete-with-reconnect, branch-descendant
 * collection, new-step attachment — rather than each carrying its own copy.
 *
 * Every function here is pure: it takes the current `steps` / `connections`
 * arrays plus an explicit context and returns new values. No React state, no
 * side effects — the host component owns `setState`, toasts and routing.
 */
import type { AutomationStep, StepConnection } from '../types/automation';

/**
 * One entry of the branch drill-in path (1.0) or the active branch context
 * (2.0). Identifies which branch a newly-added step should attach into.
 */
export interface BranchContextEntry {
	stepId: number;
	stepName: string;
	branchKey: string;
	branchLabel: string;
}

/**
 * Collect every step that is exclusively part of the branches under a
 * condition/parallel step — i.e. the steps that should be deleted along with
 * it. A step reached by the branch is included only when it has a single
 * incoming connection; a step with multiple incoming connections is a merge
 * point that rejoins the main flow and must be kept.
 */
export function findAllDescendants(
	startStepId: number,
	connections: Array<StepConnection>,
	steps: Array<AutomationStep>,
): Set<number> {
	const descendants = new Set<number>();
	const visited = new Set<number>();

	const traverse = (stepId: number, isRoot: boolean) => {
		if (visited.has(stepId)) {
			return;
		}

		visited.add(stepId);

		const step = steps.find((s) => s.id === stepId);

		if (step == null) {
			return;
		}

		// Get all outgoing connections from this step
		const outgoing = connections.filter((c) => c.fromStepId === stepId);

		for (const conn of outgoing) {
			// For the ROOT condition/parallel step, only follow BRANCH
			// connections. Skip "next" connections (undefined or 'next') —
			// these go to the merge point.
			if (
				isRoot &&
				(step.stepType === 'condition' || step.stepType === 'parallel')
			) {
				if (conn.conditionKey == null || conn.conditionKey === 'next') {
					continue;
				}
			}

			const targetStep = steps.find((s) => s.id === conn.toStepId);

			if (targetStep == null) {
				continue;
			}

			// Check how many incoming connections the target has
			const targetIncoming = connections.filter(
				(c) => c.toStepId === conn.toStepId,
			);

			// If target has only one incoming connection (from this branch),
			// it's part of the branch and should be deleted.
			if (targetIncoming.length === 1) {
				descendants.add(conn.toStepId);
				traverse(conn.toStepId, false);
			}
			// If target has multiple incoming connections, it's a merge point
			// and should NOT be deleted (it rejoins the main flow).
		}
	};

	// Start traversal from the step being deleted (isRoot = true).
	traverse(startStepId, true);

	return descendants;
}

/**
 * Delete a step (and, for condition/parallel steps, its branch descendants)
 * from the graph, reconnecting the step(s) that pointed at it to the step
 * that followed it so the flow stays linked.
 *
 * The caller is responsible for refusing to delete the `start` step — this
 * function does not guard against it (that's a UX concern with a toast).
 */
export function deleteStepFromGraph(
	step: AutomationStep,
	steps: Array<AutomationStep>,
	connections: Array<StepConnection>,
): { steps: Array<AutomationStep>; connections: Array<StepConnection> } {
	// Collect all steps to delete.
	const stepsToDelete = new Set<number>();
	stepsToDelete.add(step.id);

	// For condition/parallel steps, find all descendants to delete.
	if (step.stepType === 'condition' || step.stepType === 'parallel') {
		const descendants = findAllDescendants(step.id, connections, steps);
		descendants.forEach((id) => stepsToDelete.add(id));
	}

	// Find incoming connections to the root step being deleted.
	const incomingToRoot = connections.filter((c) => c.toStepId === step.id);

	// Find the "next" step after the branch (where branches merge back) —
	// a step that has incoming connections from deleted steps but is NOT
	// itself being deleted.
	let nextStepId: number | null = null;

	for (const id of stepsToDelete) {
		const outgoing = connections.filter((c) => c.fromStepId === id);

		for (const conn of outgoing) {
			if (!stepsToDelete.has(conn.toStepId)) {
				nextStepId = conn.toStepId;
				break;
			}
		}

		if (nextStepId != null) {
			break;
		}
	}

	// Remove all steps in the set.
	const nextSteps = steps.filter((s) => !stepsToDelete.has(s.id));

	// Remove all connections involving any deleted step, then reconnect
	// incoming-to-root → next step (if one exists).
	const filtered = connections.filter(
		(c) => !stepsToDelete.has(c.fromStepId) && !stepsToDelete.has(c.toStepId),
	);
	const reconnected: Array<StepConnection> = [];

	if (incomingToRoot.length > 0 && nextStepId != null) {
		for (const incoming of incomingToRoot) {
			reconnected.push({
				fromStepId: incoming.fromStepId,
				toStepId: nextStepId,
				conditionKey: incoming.conditionKey,
			} as StepConnection);
		}
	}

	return { steps: nextSteps, connections: [...filtered, ...reconnected] };
}

/**
 * Build the connection that links a newly-added step into the graph.
 *
 * Returns `null` when there is nothing to attach from (an empty flow / empty
 * branch where the new step becomes the first node).
 *
 * `branchContext` is the explicit branch path: empty = main flow. The 1.0
 * builder passes its drill-in `branchPath`; the 2.0 builder passes whichever
 * lane the add happened in. `stepsInScope` is the ordered list of steps
 * already visible in that same scope (so the new step appends after the last
 * one).
 */
export function buildAppendConnection(
	newStepId: number,
	branchContext: Array<BranchContextEntry>,
	stepsInScope: Array<AutomationStep>,
): StepConnection | null {
	if (branchContext.length > 0) {
		const parentBranch = branchContext[branchContext.length - 1];

		if (stepsInScope.length === 0) {
			// First step in the branch — connect from the parent
			// condition/parallel step using the branch key.
			if (parentBranch == null) {
				return null;
			}
			return {
				fromStepId: parentBranch.stepId,
				toStepId: newStepId,
				conditionKey: parentBranch.branchKey,
			} as StepConnection;
		}

		// Subsequent step in the branch — connect from the last step with 'next'.
		const lastVisibleStep = stepsInScope[stepsInScope.length - 1];
		if (lastVisibleStep?.id != null) {
			return {
				fromStepId: lastVisibleStep.id,
				toStepId: newStepId,
				conditionKey: 'next',
			} as StepConnection;
		}
		return null;
	}

	// Main flow — connect from the last step.
	const lastStep = stepsInScope[stepsInScope.length - 1];
	if (lastStep?.id != null) {
		return {
			fromStepId: lastStep.id,
			toStepId: newStepId,
			conditionKey: 'next',
		} as StepConnection;
	}
	return null;
}
